<template>
  <aside class="sidebar" :class="{ 'sidebar--collapsed': sidebarCollapsed }">
    <div class="sidebar-top">
      <div class="sidebar-header" v-if="!isWhiteLabel">
        <router-link 
          :to="isLogged ? (auth.isAdmin ? '/admin/dashboard' : '/my-account/dashboard') : '/'" 
          class="logo"
          aria-label="Página inicial ou Dashboard"
        >
          <AppLogo v-if="!sidebarCollapsed" :height="60" class="sidebar-logo" />
          <AppLogoIcon v-else :height="36" class="sidebar-logo-icon" />
        </router-link>
        <button class="sidebar-collapse-btn" @click="toggleSidebar" :title="sidebarCollapsed ? 'Expandir menu' : 'Minimizar menu'">
          <i class="fas" :class="sidebarCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'"></i>
        </button>
      </div>

      <nav class="nav-links">
        <div v-if="['event', 'checkout', 'cart', 'payment-success'].includes(page)" class="event-nav">
          <!-- Identidade do evento -->
          <div v-if="eventData" class="event-identity">
            <img v-if="eventData.baby_photo" :src="eventData.baby_photo" class="event-avatar" alt="Foto do evento" />
            <div v-else class="event-avatar-placeholder"><i class="fas fa-gift"></i></div>
            <div class="event-identity-info">
              <span class="event-identity-name">{{ eventData.baby_name }}</span>
              <span class="event-identity-type">{{ eventData.event_type }}</span>
            </div>
          </div>

          <!-- Links de navegação -->
          <router-link :to="`/event/${eventId}`" class="event-nav-link">
            <i class="fas fa-gift me-2"></i>Lista de Presentes
          </router-link>
          <a
            href="#"
            class="event-nav-link"
            @click.prevent="$router.push(`/event/${eventId}?view=convite`)"
          >
            <i class="fas fa-envelope-open-text me-2"></i>Ver convite
          </a>
          <a
            v-if="page !== 'cart' && page !== 'payment-success' && !isEventUnavailableByOwnerActivation"
            href="#"
            class="event-nav-link"
            @click.prevent="handleGoToCart"
          >
            <i class="fas fa-basket-shopping me-2"></i>Ir para o carrinho
          </a>
        </div>
        <div v-else-if="page === 'payment'" class="payment-escape-nav">
          <router-link :to="`/event/${eventId}`" class="payment-escape-link">
            <i class="fas fa-chevron-left me-2"></i>Voltar para a lista
          </router-link>

          <div v-if="presenteador?.name" class="sidebar-info-card sidebar-giver-card">
            <p class="sidebar-info-label">
              <i class="fa-solid fa-user-circle me-1"></i> Presenteador
            </p>
            <div class="sidebar-giver-row">
              <div class="sidebar-giver-avatar">{{ inicialPresenteador }}</div>
              <div class="sidebar-giver-info">
                <span class="sidebar-giver-name">{{ primeiroNome }}</span>
                <span class="sidebar-giver-email">{{ presenteador.email }}</span>
              </div>
            </div>
          </div>

          <div v-if="checkoutEventData" class="sidebar-info-card sidebar-event-card">
            <div class="sidebar-event-header">
              <div class="sidebar-event-photo">
                <img
                  v-if="checkoutEventData.baby_photo"
                  :src="checkoutEventData.baby_photo"
                  :alt="checkoutEventData.baby_name"
                />
                <div v-else class="sidebar-event-photo-placeholder">
                  <i class="fas fa-gift"></i>
                </div>
              </div>
              <div class="sidebar-event-identity">
                <span class="sidebar-event-type">{{ checkoutEventData.event_type }}</span>
                <p class="sidebar-event-name">{{ checkoutEventData.baby_name || checkoutEventData.name }}</p>
              </div>
            </div>

            <div class="sidebar-divider"></div>

            <p class="sidebar-info-label mb-2">
              <i class="fa-solid fa-basket-shopping me-1"></i>
              {{ cartItems.length }} {{ cartItems.length === 1 ? 'presente' : 'presentes' }}
            </p>
            <ul class="sidebar-items-list">
              <li
                v-for="item in cartItems"
                :key="item.id"
                class="sidebar-item-row"
              >
                <div class="sidebar-item-thumb">
                  <img v-if="item.image_url || item.imageUrl" :src="item.image_url || item.imageUrl" :alt="item.name" />
                  <i v-else class="fas fa-gift sidebar-item-icon"></i>
                </div>
                <div class="sidebar-item-info">
                  <span class="sidebar-item-name">{{ item.name }}</span>
                  <span class="sidebar-item-qty">{{ item.quantity }}x</span>
                </div>
                <span class="sidebar-item-price">
                  {{ (item.price * item.quantity).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}
                </span>
              </li>
            </ul>

            <div class="sidebar-total-row">
              <span class="sidebar-total-label">Total</span>
              <span class="sidebar-total-value">{{ totalFormatado }}</span>
            </div>

          </div>
        </div>
        <div v-else-if="isLogged">
            <!-- ── Minha Conta (organizador) ── -->
            <template v-if="!auth.isAdmin">
              <p class="nav-group-label">Minha Conta</p>
              <router-link to="/my-account/dashboard" class="nav-item" data-tooltip="Início">
                <i class="fas fa-house nav-item__icon"></i>
                <span class="nav-item__label">Início</span>
              </router-link>
              <router-link to="/my-account/events" class="nav-item" data-tooltip="Meus Eventos">
                <i class="fas fa-calendar-check nav-item__icon"></i>
                <span class="nav-item__label">Meus Eventos</span>
              </router-link>
              <router-link to="/finance" class="nav-item" data-tooltip="Minhas Finanças">
                <i class="fas fa-wallet nav-item__icon"></i>
                <span class="nav-item__label">Minhas Finanças</span>
              </router-link>
            </template>

            <!-- ── Administrativo (somente role admin) ── -->
            <template v-if="auth.isAdmin">
              <p class="nav-group-label">Administrativo</p>
              <router-link to="/admin/dashboard" class="nav-item" data-tooltip="Início">
                <i class="fas fa-house nav-item__icon"></i>
                <span class="nav-item__label">Início</span>
              </router-link>
              <router-link to="/admin/withdrawals" class="nav-item" data-tooltip="Extrato de Saques">
                <i class="fas fa-receipt nav-item__icon"></i>
                <span class="nav-item__label">Extrato de Saques</span>
              </router-link>
              <router-link to="/admin/withdrawal-requests" class="nav-item" data-tooltip="Solicitações de Saque">
                <i class="fas fa-hand-holding-dollar nav-item__icon"></i>
                <span class="nav-item__label">Solicitações de Saque</span>
              </router-link>
              <router-link to="/admin/finance" class="nav-item" data-tooltip="Dashboard Financeiro">
                <i class="fas fa-chart-line nav-item__icon"></i>
                <span class="nav-item__label">Dashboard Financeiro</span>
              </router-link>
              <router-link to="/admin/event-payouts" class="nav-item" data-tooltip="Eventos e Repasses">
                <i class="fas fa-money-bill-wave nav-item__icon"></i>
                <span class="nav-item__label">Eventos e Repasses</span>
              </router-link>
              <router-link to="/admin/catalog" class="nav-item" data-tooltip="Catálogo Padrão">
                <i class="fas fa-list nav-item__icon"></i>
                <span class="nav-item__label">Catálogo Padrão</span>
              </router-link>
              <router-link to="/admin/legal" class="nav-item" data-tooltip="Documentos Legais">
                <i class="fas fa-file-contract nav-item__icon"></i>
                <span class="nav-item__label">Documentos Legais</span>
              </router-link>
            </template>
        </div>

        <div v-else class="public-nav">
          <p class="nav-group-label">Menu</p>
          <router-link to="/" class="nav-item" data-tooltip="Início">
            <i class="fas fa-home nav-item__icon"></i>
            <span class="nav-item__label">Início</span>
          </router-link>
          <router-link to="/how-it-works" class="nav-item" data-tooltip="Como Funciona">
            <i class="fas fa-circle-question nav-item__icon"></i>
            <span class="nav-item__label">Como Funciona</span>
          </router-link>
          <router-link to="/login" class="nav-item" data-tooltip="Entrar">
            <i class="fas fa-right-to-bracket nav-item__icon"></i>
            <span class="nav-item__label">Entrar</span>
          </router-link>
          <router-link to="/register" class="nav-item" data-tooltip="Cadastrar">
            <i class="fas fa-user-plus nav-item__icon"></i>
            <span class="nav-item__label">Cadastrar</span>
          </router-link>
        </div>
      </nav>
    </div>

    <div class="sidebar-base">
      <a v-if="isEventMode && !isEventUnavailableByOwnerActivation" class="icon-cart" href="#" @click.prevent="handleGoToCart">
        <CartIcon :eventId="`${eventId}`" />
      </a>

      <router-link
        v-if="showCreateEventBtn"
        to="/my-account/add"
        class="btn-criar-evento"
      >
        <i class="fas fa-plus-circle me-2"></i>Criar Evento
      </router-link>

      <router-link
        v-if="!isLogged"
        to="/my-account/add"
        class="btn-criar-evento btn-public-cta"
        data-tooltip="Criar minha lista"
      >
        <i class="fas fa-gift me-2 btn-public-cta__icon"></i><span class="btn-public-cta__label">Criar minha lista</span>
      </router-link>

      <div class="sidebar-separator">
        <div class="separator-line"></div>
      </div>

      <div
        v-if="isLogged && page !== 'payment'"
        class="profile-card-wrapper"
        ref="profileMenuRef"
      >
        <div v-if="showProfileMenu" class="profile-menu-popover">
          <button type="button" class="profile-menu-item" @click="goToProfile">Editar Perfil</button>
          <button type="button" class="profile-menu-item" @click="goToPix">Dados Bancarios</button>
          <button type="button" class="profile-menu-item" @click="openChangePasswordModal">Alterar Senha</button>
          <button type="button" class="profile-menu-item profile-menu-item--danger" @click="openLogoutConfirmation">Sair</button>
        </div>

        <button type="button" class="guest-card profile-card-trigger" @click.stop="toggleProfileMenu">
          <div class="guest-card-avatar">
            <img v-if="userAvatarURL" :src="userAvatarURL" :alt="userInitial" class="guest-card-avatar-img" referrerpolicy="no-referrer" />
            <template v-else>{{ userInitial }}</template>
          </div>
          <div class="guest-card-info">
            <span class="guest-card-name">{{ userDisplayName }}</span>
            <span class="guest-card-label" :title="userDisplayEmail">{{ userDisplayEmail }}</span>
          </div>
          <i class="fas fa-chevron-right guest-card-arrow"></i>
        </button>
      </div>

      <ChangePasswordModal
        :visible="showChangePasswordModal"
        @close="showChangePasswordModal = false"
      />

      <BottomModal
        :visible="showLogoutConfirmation"
        @close="showLogoutConfirmation = false"
        :fullHeight="false"
      >
        <template #default>
          <div class="confirmation-content p-3 text-center">
            <h5 class="mb-3 text-primary-emphasis">Confirmar Saída?</h5>
            <p class="text-muted mb-4">
              Ao sair, você será desconectado(a). Na sua próxima visita, será necessário informar sua senha para acessar sua conta novamente.
            </p>
            <div class="d-flex justify-content-center gap-3 mt-4">
              <button class="btn btn-sm btn-outline-secondary" @click="showLogoutConfirmation = false">
                <i class="fa-solid fa-chevron-down me-2"></i>
              </button>
              <button class="btn btn-sm btn-color-site" @click="confirmLogout">
                <span v-if="isLoggingOut" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {{ isLoggingOut ? 'Saindo...' : 'Confirmar Saída' }}
              </button>
            </div>
          </div>
        </template>
      </BottomModal>

      <BottomModal
        :visible="showEmptyCartModal"
        @close="showEmptyCartModal = false"
        :fullHeight="false"
      >
        <template #default>
          <div class="confirmation-content p-3 text-center">
            <div class="mb-3" style="font-size: 2.5rem; color: var(--event-primary, #e91e8c);">
              <i class="fas fa-basket-shopping"></i>
            </div>
            <h5 class="mb-2">Carrinho vazio</h5>
            <p class="text-muted mb-4">
              Você ainda não selecionou nenhum presente. Escolha os itens que deseja presentear na lista de presentes.
            </p>
            <div class="d-flex justify-content-center gap-3">
              <button class="btn btn-sm btn-outline-secondary" @click="showEmptyCartModal = false">
                <i class="fa-solid fa-chevron-down me-2"></i>Fechar
              </button>
              <router-link :to="`/event/${eventId}`" class="btn btn-sm btn-color-site" @click="showEmptyCartModal = false">
                <i class="fas fa-gift me-2"></i>Ver presentes
              </router-link>
            </div>
          </div>
        </template>
      </BottomModal>

      <div v-if="!isLogged" class="extra-links">
        <router-link to="/termos-de-uso">Termos de Uso</router-link>
        <router-link to="/politica-de-privacidade">Política de Privacidade</router-link>
        <router-link to="/termos-de-saque">Regras de Saque</router-link>
        <router-link to="/contact">Contato</router-link>
      </div>

      <div v-if="!isLogged" class="social-icons">
        <a href="https://www.facebook.com/quemimoapp" 
          target="_blank" rel="noopener noreferrer" aria-label="Siga-nos no Facebook">
            <i class="fa-brands fa-facebook-f"></i>
        </a>
        <a href="https://www.instagram.com/quemimoapp" 
          target="_blank" rel="noopener noreferrer" aria-label="Siga-nos no Instagram">
            <i class="fa-brands fa-instagram"></i>
        </a>
        <a href="https://www.threads.net/@quemimoapp" 
          target="_blank" rel="noopener noreferrer" aria-label="Siga-nos no Threads" class="social-icon-svg">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192 192" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M141.537 88.988a66.667 66.667 0 0 0-2.518-1.143c-1.482-27.307-16.403-42.94-41.457-43.1h-.34c-14.986 0-27.449 6.396-35.12 18.036l13.779 9.452c5.73-8.695 14.724-10.548 21.348-10.548h.23c8.248.054 14.474 2.452 18.515 7.129 2.932 3.405 4.893 8.111 5.864 14.05-7.314-1.243-15.224-1.626-23.68-1.14-23.82 1.371-39.134 15.264-38.105 34.568.522 9.792 5.4 18.216 13.735 23.719 7.047 4.652 16.124 6.927 25.557 6.412 12.458-.683 22.231-5.436 29.05-14.127 5.177-6.6 8.452-15.153 9.898-25.93 5.937 3.583 10.337 8.298 12.767 13.966 4.132 9.635 4.373 25.468-8.546 38.376-11.319 11.308-24.925 16.2-45.488 16.351-22.809-.169-40.06-7.484-51.275-21.742C35.236 139.966 29.808 120.682 29.605 96c.203-24.682 5.63-43.966 16.133-57.317C56.954 24.425 74.204 17.11 97.013 16.94c22.975.17 40.526 7.52 52.171 21.847 5.71 7.026 10.015 15.86 12.853 26.162l16.147-4.308c-3.44-12.68-8.853-23.606-16.219-32.668C147.036 9.607 125.202.195 97.07 0h-.113C68.882.195 47.292 9.643 32.788 28.08 19.882 44.485 13.224 67.315 13.001 96v.027c.223 28.682 6.88 51.512 19.787 67.916 14.504 18.438 36.094 27.886 64.174 28.08h.113c24.999-.169 42.557-6.727 57.03-21.19 18.963-18.945 18.392-42.692 12.142-57.27-4.484-10.454-13.033-18.945-24.71-24.575Zm-43.096 40.52c-10.426.576-21.263-4.095-21.82-14.079-.427-8.013 5.701-16.936 24.084-18.008 2.105-.122 4.17-.179 6.197-.179 6.303 0 12.196.609 17.587 1.774-2.003 24.993-15.51 29.888-26.048 30.492Z"/></svg>
        </a>
      </div>
    </div>
  </aside>
</template>

<script>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlertStore } from '@/stores/alertStore';
import { useAuthStore } from '@/stores/authStore';
import { useEventStore } from '@/stores/eventStore';
import { useCustomerStore } from '@/stores/customerStore';
import { useCartStore } from '@/stores/cartStore';
import { useThemeStore } from '@/stores/themeStore';
import CartIcon from '@/components/CartIcon.vue';
import ChangePasswordModal from '@/components/ChangePasswordModal.vue';
import BottomModal from '@/components/BottomModal.vue';
import AppLogo from '@/components/AppLogo.vue';
import AppLogoIcon from '@/components/AppLogoIcon.vue';


export default {
  props: {
    page: { type: String, default: '' },
    eventId: { type: String, default: '' },
    forceCollapsed: { type: Boolean, default: false },
  },
  components: {
    CartIcon,
    ChangePasswordModal,
    BottomModal,
    AppLogo,
    AppLogoIcon,
  },
  setup(props) {
    const auth = useAuthStore();
    const eventStore = useEventStore();
    const customerStore = useCustomerStore();
    const cart = useCartStore();
    const themeStore = useThemeStore();
    const route = useRoute();
    const router = useRouter();
    const alert = useAlertStore();

    const showProfileMenu = ref(false);
    const showChangePasswordModal = ref(false);
    const showLogoutConfirmation = ref(false);
    const showEmptyCartModal = ref(false);
    const isLoggingOut = ref(false);

    function handleGoToCart() {
      const items = cart.getEvent(eventId.value)?.items || [];
      if (items.length === 0) {
        showEmptyCartModal.value = true;
      } else {
        router.push(`/checkout/${eventId.value}`);
      }
    }
    const profileMenuRef = ref(null);
    const isLogged = computed(() => auth.isAuthenticated);

    // ── Sidebar colapsável ───────────────────────────────────────────
    const sidebarCollapsed = ref(props.forceCollapsed || localStorage.getItem('sidebar:collapsed') === 'true');

    watch(() => props.forceCollapsed, (val) => {
      if (val) sidebarCollapsed.value = true;
    });

    function toggleSidebar() {
      sidebarCollapsed.value = !sidebarCollapsed.value;
      localStorage.setItem('sidebar:collapsed', sidebarCollapsed.value);
    }

    const isEventMode = computed(() => eventStore.isEventMode);
    const eventData = computed(() => eventStore.eventData);

    const OWNER_ACTIVE_FLAG_CANDIDATES = [
      'owner_is_active',
      'ownerIsActive',
      'is_owner_active',
      'owner_active',
    ];

    const OWNER_UNAVAILABLE_REASON_CANDIDATES = [
      'event_unavailable_reason',
      'eventUnavailableReason',
      'unavailable_reason',
      'unavailableReason',
    ];

    const readEventBoolean = (payload, fields) => {
      if (!payload || typeof payload !== 'object') return null;

      const candidates = [
        payload,
        payload.event,
        payload.data,
        payload.owner,
        payload.event?.owner,
      ].filter(Boolean);

      for (const source of candidates) {
        for (const field of fields) {
          if (typeof source[field] === 'boolean') {
            return source[field];
          }
        }
      }

      return null;
    };

    const readEventReason = (payload, fields) => {
      if (!payload || typeof payload !== 'object') return '';

      const candidates = [
        payload,
        payload.event,
        payload.data,
      ].filter(Boolean);

      for (const source of candidates) {
        for (const field of fields) {
          if (typeof source[field] === 'string' && source[field].trim()) {
            return source[field].trim();
          }
        }
      }

      return '';
    };

    const isEventUnavailableByOwnerActivation = computed(() => {
      if (!isEventMode.value || !eventData.value) return false;

      const ownerActive = readEventBoolean(eventData.value, OWNER_ACTIVE_FLAG_CANDIDATES);
      if (ownerActive === false) return true;

      const reason = readEventReason(eventData.value, OWNER_UNAVAILABLE_REASON_CANDIDATES);
      return reason === 'OWNER_NOT_ACTIVE';
    });
    const eventId = computed(() => {
      if (props.eventId) return props.eventId;
      const path = route.path;
      const id = route.params.id;

      if (path.startsWith('/event/') || path.startsWith('/cart/') || path.startsWith('/checkout/')
    || path.startsWith('/payment-success/')) {
        return id;
      }

      return null;
    });

    const page = computed(() => {
      if (props.page) return props.page;
      const path = route.path;

      if (path.startsWith('/event/')) return 'event';
      if (path.startsWith('/cart/')) return 'cart';
      if (path.startsWith('/checkout/') && (path.endsWith('/pay') || route.name === 'PaymentView')) return 'payment';
      if (path.startsWith('/checkout/')) return 'checkout';
      if (path.startsWith('/my-account')) return 'account';
      if (path.startsWith('/payment-success')) return 'payment-success';

      return 'default';
    });

    const presenteador = computed(() => customerStore.activeCustomer || null);

    const checkoutEventData = computed(() => {
      if (!eventId.value) return null;
      return cart.getEvent(eventId.value) || null;
    });

    const cartItems = computed(() => checkoutEventData.value?.items || []);

    const totalFormatado = computed(() => {
      const total = cartItems.value.reduce((sum, item) => {
        const price = Number(item.price) || 0;
        const quantity = Number(item.quantity) || 0;
        return sum + (price * quantity);
      }, 0);
      return total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    });

    const inicialPresenteador = computed(() => {
      const name = presenteador.value?.name || '';
      return name.charAt(0).toUpperCase();
    });

    const primeiroNome = computed(() => {
      return presenteador.value?.name?.split(' ')[0] || '';
    });

    const userDisplayName = computed(() => auth.user?.name || 'Usuário');
    const userDisplayEmail = computed(() => auth.user?.email || 'Organizador');
    const userAvatarURL = computed(() => auth.user?.avatar_url || '');
    const userInitial = computed(() => {
      const normalized = userDisplayName.value.trim();
      return (normalized.charAt(0) || 'U').toUpperCase();
    });

    const closeProfileMenu = () => {
      showProfileMenu.value = false;
    };

    const toggleProfileMenu = () => {
      showProfileMenu.value = !showProfileMenu.value;
    };

    const goToProfile = () => {
      showProfileMenu.value = false;
      router.push('/my-account/profile');
    };

    const goToPix = () => {
      showProfileMenu.value = false;
      router.push('/my-account/bank');
    };

    const openChangePasswordModal = () => {
      showProfileMenu.value = false;
      showChangePasswordModal.value = true;
    };

    const openLogoutConfirmation = () => {
      showProfileMenu.value = false;
      showLogoutConfirmation.value = true;
    };

    const confirmLogout = async () => {
      if (isLoggingOut.value) return;

      isLoggingOut.value = true;
      alert.loading(true);
      auth.logout();
      router.push('/login');
      alert.close();
      showLogoutConfirmation.value = false;
      isLoggingOut.value = false;
    };

    const handleOutsideClick = (event) => {
      if (!profileMenuRef.value) return;
      if (!profileMenuRef.value.contains(event.target)) {
        closeProfileMenu();
      }
    };

    onMounted(() => {
      document.addEventListener('click', handleOutsideClick);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleOutsideClick);
    });


    return {
      auth,
      isLogged,
      isEventMode,
      eventId,
      eventData,
      presenteador,
      checkoutEventData,
      cartItems,
      totalFormatado,
      inicialPresenteador,
      primeiroNome,
      userDisplayName,
      userDisplayEmail,
      userAvatarURL,
      userInitial,
      page,
      customerStore,
      showProfileMenu,
      showChangePasswordModal,
      showLogoutConfirmation,
      isLoggingOut,
      profileMenuRef,
      toggleProfileMenu,
      openChangePasswordModal,
      openLogoutConfirmation,
      confirmLogout,
      goToProfile,
      goToPix,
      isEventUnavailableByOwnerActivation,
      sidebarCollapsed,
      toggleSidebar,
      showEmptyCartModal,
      handleGoToCart,
      themeStore,
      showCreateEventBtn: computed(() => auth.isAuthenticated && !auth.isAdmin && !route.path.startsWith('/my-account/add') && !route.path.startsWith('/my-account/dashboard') && !route.path.startsWith('/cart/') && !route.path.startsWith('/checkout/') && !isEventMode.value),
      isWhiteLabel: computed(() => eventStore.whiteLabel)
    };
  }
};
</script>


<style scoped>
/* Seus estilos CSS permanecem inalterados */
.router-link-active {
  color: var(--event-primary, #f78da7);
  font-weight: bold;
}

.sidebar {
  color: var(--panel-sidebar-text, #32355d);
  padding: 46px 35px 10px;
  box-sizing: border-box;
  height: 100vh;
  background-color: var(--panel-sidebar-bg, #fdfcff);
  border-right: 1px solid var(--panel-border, #f0edf8);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: width 0.25s ease, padding 0.25s ease;
  overflow: hidden;
  width: 270px;
  flex-shrink: 0;
}

/* ── Sidebar colapsada ──────────────────────────────── */
.sidebar--collapsed {
  width: 64px;
  padding: 20px 12px 10px;
  align-items: center;
  overflow: visible;
}

.sidebar--collapsed .sidebar-top {
  align-items: center;
  width: 100%;
}

.sidebar--collapsed .btn-criar-evento,
.sidebar--collapsed .sidebar-separator {
  display: none;
}

/* Collapsed: ícone + tooltip CSS */
.sidebar--collapsed .nav-group-label {
  display: none;
}

.sidebar--collapsed .nav-item {
  justify-content: center;
  padding: 10px 0;
  position: relative;
}

.sidebar--collapsed .nav-item__label {
  display: none;
}

.sidebar--collapsed .nav-item[data-tooltip]::after {
  content: attr(data-tooltip);
  position: absolute;
  left: calc(100% + 14px);
  top: 50%;
  transform: translateY(-50%);
  background: #32355d;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 6px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s;
  z-index: 200;
}

.sidebar--collapsed .nav-item[data-tooltip]:hover::after {
  opacity: 1;
}

/* Em collapsed, oculta extra-links e social-icons */
.sidebar--collapsed .extra-links,
.sidebar--collapsed .social-icons {
  display: none;
}

/* Em collapsed, mostra CTA publico so como icone com tooltip */
.sidebar--collapsed .btn-public-cta {
  display: flex !important;
  align-items: center;
  justify-content: center;
  width: 40px;
  padding: 8px;
  border-radius: 8px;
  margin: 0 auto;
  font-size: 1rem;
}
.sidebar--collapsed .btn-public-cta .me-2 {
  margin-right: 0 !important;
}
.sidebar--collapsed .btn-public-cta__label {
  display: none;
}

/* Em collapsed, mostra social-icons em coluna centralizada */
.sidebar--collapsed .social-icons {
  display: flex !important;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  margin-top: 4px;
}

/* Em collapsed, oculta textos do guest-card mas mantém avatar */
.sidebar--collapsed .guest-card-info,
.sidebar--collapsed .guest-card-arrow {
  display: none;
}

.sidebar--collapsed .guest-card {
  justify-content: center;
  padding: 6px;
  border: none;
  background: transparent;
}

.sidebar--collapsed .sidebar-base {
  align-items: center;
  padding-bottom: 16px;
}

/* ── Header do sidebar (logo + botão colapsar) ─────── */
.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 8px;
}

.sidebar--collapsed .sidebar-header {
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.sidebar-logo-icon {
  display: block;
}

.sidebar-collapse-btn {
  background: none;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #c0c0c0;
  font-size: 0.65rem;
  flex-shrink: 0;
  transition: color 0.2s, background 0.2s;
}

.sidebar-collapse-btn:hover {
  color: var(--event-primary, #ff50a3);
  background: var(--event-primary-light, #fff0f6);
}

/* ── Nav flat (sem accordion) ──────────────────────── */
.nav-group-label {
  font-size: 20px;
  font-weight: bold;
  color: var(--panel-sidebar-text, #32355d);
  padding: 0 8px;
  margin: 4px 0 4px;
  user-select: none;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border-radius: 8px;
  text-decoration: none;
  color: var(--panel-text-muted, #7e7f83);
  font-size: 1.1rem;
  font-weight: initial;
  line-height: 1.2;
  white-space: nowrap;
  transition: color 0.2s, background-color 0.2s, padding-left 0.2s;
  margin: 1px 0;
}

.nav-item:hover,
.nav-item.router-link-active {
  color: var(--event-primary, #ff50a3);
  background-color: var(--event-primary-light, #fff0f6);
}

.nav-item__icon {
  font-size: 0.9rem;
  color: #c0c0c0;
  width: 18px;
  text-align: center;
  flex-shrink: 0;
  transition: color 0.2s;
}

.nav-item:hover .nav-item__icon,
.nav-item.router-link-active .nav-item__icon {
  color: var(--event-primary, #ff50a3);
}

.nav-item__label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-top {
  display: flex;
  flex-direction: column;
}

.logo {
  margin-bottom: 20px;
}

.icon-cart{
  text-decoration: none;
}

.logo img {
  width: 120px;
  object-fit: contain;
}

.nav-links a:not(.nav-item) {
  font-size: 20px;
  display: block;
  color: #32355d;
  font-weight: bold;
  text-decoration: none;
  margin: 10px 0;
  transition: color 0.3s;
}

.nav-links a:not(.nav-item):hover {
  color: var(--event-primary, #f78da7);
}

.nav-links a.router-link-exact-active {
  color: var(--event-primary, #f78da7);
}

.public-nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 8px;
}

.public-nav__link {
  display: flex !important;
  align-items: center;
  gap: 8px;
  font-size: 1rem !important;
  font-weight: 500 !important;
  color: #32355d !important;
  text-decoration: none;
  padding: 7px 8px;
  border-radius: 8px;
  margin: 0 !important;
  transition: color 0.2s, background-color 0.2s;
}

.public-nav__link:hover,
.public-nav__link.router-link-exact-active {
  color: var(--event-primary, #ff50a3) !important;
  background-color: var(--event-primary-light, #fff0f6);
}

.public-nav__icon {
  font-size: 0.85rem;
  color: #c0c0c0;
  width: 16px;
  text-align: center;
  flex-shrink: 0;
  transition: color 0.2s;
}

.public-nav__link:hover .public-nav__icon,
.public-nav__link.router-link-exact-active .public-nav__icon {
  color: var(--event-primary, #ff50a3);
}

.public-nav__divider {
  height: 1px;
  background: #ececec;
  margin: 10px 0;
}

.public-nav__cta {
  display: flex !important;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--event-primary, #ff50a3), var(--event-primary, #e6408f));
  color: #fff !important;
  text-decoration: none;
  font-weight: 700 !important;
  font-size: 0.9rem !important;
  padding: 0.65rem 1rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px var(--event-primary-shadow, rgba(255, 80, 163, 0.35));
  margin: 0 !important;
  transition: background 0.2s, transform 0.15s;
}

.public-nav__cta:hover {
  background: linear-gradient(135deg, var(--event-primary, #e6408f), var(--event-primary, #c73580));
  transform: translateY(-1px);
}

.payment-escape-nav {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0 0.25rem;
  margin-top: 0.5rem;
}

.payment-escape-nav a.payment-escape-link {
  display: flex;
  align-items: center;
  font-size: 0.95rem;
  font-weight: 600;
  color: #6c757d;
  text-decoration: none;
  padding: 0.5rem 0;
  transition: color 0.2s;
}

.payment-escape-nav a.payment-escape-link:hover {
  color: var(--event-primary, #ff50a3);
}

.sidebar-info-card {
  background: #fff;
  border: 1px solid #f0e6f6;
  border-radius: 1rem;
  padding: 1rem;
  width: 100%;
}

.sidebar-giver-row {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-top: 0.5rem;
}

.sidebar-giver-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--event-primary, #ff50a3), var(--event-primary, #ff8fd1));
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sidebar-giver-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.sidebar-giver-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-giver-email {
  font-size: 0.75rem;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-event-header {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.75rem;
}

.sidebar-event-photo {
  width: 44px;
  height: 44px;
  border-radius: 0.65rem;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid #ffd6eb;
}

.sidebar-event-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sidebar-event-photo-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #fce7f3, #fdf2f8);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--event-primary, #ff50a3);
  font-size: 1.1rem;
}

.sidebar-event-identity {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.sidebar-event-type {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--event-primary, #ff50a3);
  background: var(--event-primary-light, #fdf2f8);
  border: 1px solid color-mix(in srgb, var(--event-primary, #ff50a3) 25%, transparent);
  border-radius: 999px;
  padding: 0.1rem 0.5rem;
  display: inline-block;
  margin-bottom: 0.2rem;
  width: fit-content;
}

.sidebar-event-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-divider {
  height: 1px;
  background: #f3e8ff;
  margin: 0.65rem 0;
}

.sidebar-info-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9ca3af;
  margin: 0;
}

.sidebar-items-list {
  list-style: none;
  padding: 0;
  margin: 0 0 0.65rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.sidebar-item-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.sidebar-item-thumb {
  width: 32px;
  height: 32px;
  border-radius: 0.5rem;
  overflow: hidden;
  flex-shrink: 0;
  background: #fdf2f8;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #fce7f3;
}

.sidebar-item-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sidebar-item-icon {
  color: var(--event-primary, #ff50a3);
  font-size: 0.8rem;
}

.sidebar-item-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.sidebar-item-name {
  font-size: 0.8rem;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-item-qty {
  font-size: 0.7rem;
  color: #9ca3af;
}

.sidebar-item-price {
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  flex-shrink: 0;
}

.sidebar-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--event-primary-light, #fdf2f8);
  border-radius: 0.65rem;
  padding: 0.5rem 0.75rem;
  margin-top: 0.25rem;
}

.sidebar-total-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #6b7280;
}

.sidebar-total-value {
  font-size: 0.95rem;
  font-weight: 800;
  color: var(--event-primary, #ff50a3);
}

.sidebar-separator {
  display: flex;
  justify-content: center;
  padding: 12px 0;
}

.separator-line {
  width: 100%;
  border-bottom: 1px solid var(--panel-border, #ececec);
}


.sidebar-base {
  display: flex;
  flex-direction: column;
  padding-bottom: 20px;
  margin-top: auto;
  padding-top: 1.5rem;
}

.extra-links a:first-of-type  {
  margin-top: 0px !important;
}

.extra-links a {
  display: block;
  color: #8f9093;
  text-decoration: none;
  margin: 5px 0;
  font-size: 0.875rem;
  transition: color 0.2s;
}

.extra-links a:hover {
  color: var(--event-primary, #f78da7);
}

.social-icons {
  margin-top: 12px;
  display: flex;
  gap: 4px;
}

.social-icons a {
  color: #32355d;
  font-size: 1.1rem;
  padding: 6px 8px;
  border-radius: 8px;
  transition: color 0.2s, background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

.social-icons a:hover {
  color: var(--event-primary, #f78da7);
  background-color: var(--event-primary-light, #fff0f6);
}

.social-icons .social-icon-svg svg {
  display: block;
}

.dropdown-menu1 {
  margin-top: 0px;
  padding: 0px;
  list-style: none;
}

.btn-criar-evento {
  display: block;
  background-color: var(--event-primary, #ff50a3);
  color: #fff !important;
  text-decoration: none;
  text-align: center;
  padding: 0.65rem 1rem;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 4px;
  transition: background-color 0.2s, transform 0.15s;
}

.btn-criar-evento:hover {
  background-color: #e0408e;
  transform: translateY(-1px);
}

.dropdown-menu1 li:last-child {
  margin-bottom: 0;
}

.dropdown-menu1 a,
.logout {
  font-weight: initial;
  font-size: 1.1rem;
  margin: 1px 0;
  text-decoration: none;
  color: #7e7f83;
  cursor: pointer;
  display: flex;
  align-items: center;
  line-height: 1.2;
  gap: 6px;
  padding: 5px 8px;
  border-radius: 8px;
  white-space: nowrap;
  transition: color 0.2s, background-color 0.2s, padding-left 0.2s;
}

.dropdown-menu1 a:hover {
  color: var(--event-primary, #f78da7);
  background-color: var(--event-primary-light, #fff0f6);
  padding-left: 16px;
}

.nav-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  font-size: 0.65rem;
  line-height: 1;
  color: #c0c0c0;
  flex-shrink: 0;
  transition: color 0.2s, transform 0.2s;
}

.dropdown-menu1 a:hover .nav-arrow {
  color: var(--event-primary, #f78da7);
  transform: translateX(2px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ── Modo Evento ─────────────────────────────── */
.event-nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.event-identity {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--event-primary-light, #fff0f6);
  border: 1px solid color-mix(in srgb, var(--event-primary, #ff50a3) 30%, #e0e0e0);
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 10px;
}

.event-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid var(--event-primary, #ff50a3);
}

.event-avatar-placeholder {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--event-primary, #ff50a3), var(--event-primary, #ff8cc8));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.event-avatar-placeholder i {
  color: #fff;
  font-size: 1rem;
}

.event-identity-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.event-identity-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #32355d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.event-identity-type {
  font-size: 0.75rem;
  color: var(--event-primary, #ff50a3);
  font-weight: 500;
}

.event-nav-link {
  font-size: 1rem !important;
  display: flex !important;
  align-items: center;
  color: #32355d !important;
  font-weight: 600 !important;
  text-decoration: none;
  padding: 6px 4px;
  border-radius: 8px;
  transition: color 0.2s, background-color 0.2s, padding-left 0.15s;
  margin: 2px 0 !important;
}

.event-nav-link i {
  color: #c0c0c0;
  font-size: 0.9rem;
  transition: color 0.2s;
}

.event-nav-link:hover,
.event-nav-link.router-link-exact-active {
  color: var(--event-primary, #ff50a3) !important;
  background-color: var(--event-primary-light, #fff0f6);
  padding-left: 10px;
}

.event-nav-link:hover i,
.event-nav-link.router-link-exact-active i {
  color: var(--event-primary, #ff50a3);
}

/* ── Card do guest comprador ── */
.guest-card {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  background: var(--event-primary-light, #fff8fc);
  border: 1.5px solid color-mix(in srgb, var(--event-primary, #ff50a3) 30%, #e8e8e8);
  border-radius: 0.875rem;
  padding: 0.75rem 0.875rem;
  text-decoration: none;
  transition: border-color 0.2s, background 0.2s;
  margin-bottom: 8px;
}
.guest-card:hover {
  border-color: var(--event-primary, #ff50a3);
  background: var(--event-primary-light, #fff0f7);
}
.guest-card-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--event-primary, #ff50a3);
  color: #fff;
  font-size: 0.9rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}
.guest-card-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}
.guest-card-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}
.guest-card-name {
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--panel-sidebar-text, #32355d);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}
.guest-card-label {
  display: block;
  font-size: 0.75rem;
  color: var(--event-primary, #ff50a3);
  font-weight: 600;
  line-height: 1.2;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.guest-card-arrow {
  font-size: 0.65rem;
  color: #adb5bd;
  align-self: center;
  flex-shrink: 0;
  transition: color 0.2s, transform 0.2s;
}
.guest-card:hover .guest-card-arrow {
  color: var(--event-primary, #ff50a3);
  transform: translateX(2px);
}

.profile-card-wrapper {
  position: relative;
}

.profile-card-trigger {
  width: 100%;
  border: none;
  text-align: left;
}

.profile-menu-popover {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(100% + 8px);
  background: var(--panel-surface, #fff);
  border: 1px solid var(--panel-border, #f1e9f2);
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  z-index: 15;
  min-width: 160px;
}

/* Em modo colapsado, abre para a direita do avatar */
.sidebar--collapsed .profile-menu-popover {
  left: calc(100% + 8px);
  right: auto;
  bottom: 0;
  top: auto;
}

.profile-menu-item {
  border: none;
  background: transparent;
  text-align: left;
  width: 100%;
  padding: 8px 10px;
  border-radius: 8px;
  color: var(--panel-sidebar-text, #32355d);
  font-size: 0.83rem;
  font-weight: 600;
}

.profile-menu-item:hover {
  background: #fff0f7;
  color: var(--event-primary, #ff50a3);
}

.profile-menu-item--danger:hover {
  background: #fff1f1;
  color: #d14343;
}

.confirmation-content h5 { font-size: 1.25rem; font-weight: 600; }
.confirmation-content p  { font-size: 0.95rem; }

.btn-color-site {
  background-color: var(--event-primary, #ff50a3);
  color: #fff;
  border: 1px solid var(--event-primary, #ff50a3);
  padding: 0.85rem 1.5rem;
  font-size: 1.1rem;
  font-weight: bold;
  border-radius: 0.5rem;
  transition: all 0.2s ease-in-out;
}

.btn-color-site:hover:not(:disabled) {
  background-color: #e64593;
  border-color: #e64593;
  color: #fff;
}

.btn-color-site:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-outline-secondary {
  background-color: transparent;
  color: #6c757d;
  border: 1px solid #ced4da;
  padding: 0.85rem 1.5rem;
  font-size: 1.1rem;
  font-weight: bold;
  border-radius: 0.5rem;
  transition: all 0.2s ease-in-out;
}

.btn-outline-secondary:hover {
  background-color: #f8f9fa;
  border-color: #adb5bd;
  color: #495057;
}

.spinner-border {
  vertical-align: -0.125em;
}

/* ── Alternância de tema ───────────────────────────── */
.theme-toggle-row {
  display: flex;
  margin-bottom: 8px;
}

.sidebar--collapsed .theme-toggle-row {
  justify-content: center;
}

.theme-toggle-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  border-radius: 8px;
  padding: 6px 8px;
  font-size: 0.88rem;
  font-weight: 500;
  color: var(--panel-text-muted, #8f9093);
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: color 0.2s, background 0.2s;
}

.theme-toggle-btn i {
  font-size: 0.85rem;
  width: 18px;
  text-align: center;
  color: var(--panel-text-muted, #c0c0c0);
  flex-shrink: 0;
}

.theme-toggle-btn:hover {
  color: var(--primary-color, #f78da7);
  background: var(--panel-nav-active-bg, #fff0f6);
}

.theme-toggle-btn:hover i {
  color: var(--primary-color, #f78da7);
}

.theme-toggle-label {
  white-space: nowrap;
  overflow: hidden;
}

.sidebar--collapsed .theme-toggle-label {
  display: none;
}

@media (max-width: 767.98px) {
  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: auto;
    z-index: 1030;
    padding: 10px 20px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }

  .sidebar-top, .sidebar-base {
    flex-direction: column;
    align-items: flex-start;
  }

  .nav-links a {
    font-size: 18px;
    margin: 8px 0;
  }

  .public-nav {
    width: 100%;
    margin-top: 4px;
  }

  .public-nav__link {
    font-size: 0.95rem !important;
    padding: 8px;
  }

  .public-nav__cta {
    width: 100%;
    padding: 0.7rem 0.9rem;
  }

  .logo img {
    width: 140px;
  }

  .social-icons {
    justify-content: flex-start;
  }
}
</style>